import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EditableText } from "@/components/admin/EditableText";
import { EditableImage } from "@/components/admin/EditableImage";
import { useAuth } from "@/contexts/AuthContext";
import { useEditMode } from "@/contexts/EditModeContext";
import type { Brokerage } from "@/contracts/brokerage";

interface BrokerageStickyHeaderProps {
  brokerage: Brokerage;
  headerVisible: boolean;
  onRequestInfo: () => void;
  onUpdateField?: (field: string, value: any) => void;
}

export function BrokerageStickyHeader({ 
  brokerage, 
  headerVisible, 
  onRequestInfo,
  onUpdateField,
}: BrokerageStickyHeaderProps) {
  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();

  return (
    <div className={`sticky z-50 bg-background/95 backdrop-blur-md border-b shadow-lg transition-all duration-300 ease-in-out ${headerVisible ? 'top-[100px]' : 'top-0'}`}>
      <div className="container mx-auto max-w-6xl px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-3">
            {isEditor && isEditMode && onUpdateField ? (
              <EditableImage
                src={brokerage.profile_image || brokerage.logo || ''}
                alt={brokerage.name}
                onSave={async (url) => { onUpdateField('profile_image', url); }}
                containerClassName="h-10 w-10 md:h-12 md:w-12"
                className="rounded-full ring-2 ring-primary/20"
              />
            ) : (
              <Avatar className="h-10 w-10 md:h-12 md:w-12 ring-2 ring-primary/20">
                <AvatarImage src={brokerage.profile_image || brokerage.logo || undefined} alt={brokerage.name} />
                <AvatarFallback>{brokerage.name[0]}</AvatarFallback>
              </Avatar>
            )}
            <div className="max-w-[200px] md:max-w-none">
              <div className="font-bold text-xs md:text-sm truncate">
                <EditableText
                  value={brokerage.name}
                  onSave={async (value) => { onUpdateField?.('name', value); }}
                  className="inline"
                  disabled={!onUpdateField}
                />
              </div>
              {brokerage.subtitle && (
                <div className="hidden md:block text-xs text-muted-foreground truncate">
                  {brokerage.subtitle.slice(0, 50)}...
                </div>
              )}
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="#calculator" className="text-sm font-medium hover:text-primary">Calculator</a>
            <a href="#compensation" className="text-sm font-medium hover:text-primary">Compensation</a>
            <a href="#culture" className="text-sm font-medium hover:text-primary">Culture</a>
            <a href="#locations" className="text-sm font-medium hover:text-primary">Locations</a>
            <a href="#testimonials" className="text-sm font-medium hover:text-primary">Reviews</a>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <div className="hidden sm:block">
              <div className="text-xs text-muted-foreground mb-0.5">Split up to</div>
              <div className="flex items-center gap-1.5">
                <div className="text-xl font-bold">
                  {brokerage.split_max_percent ? `${brokerage.split_max_percent}%` : 'Varies'}
                </div>
              </div>
            </div>
            <Button className="font-semibold text-sm md:text-base h-9 md:h-11 shadow-md hover:shadow-lg transition-shadow" onClick={onRequestInfo}>
              Request Info
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
